/**
 * AddVideoCallMessageUseCase
 *
 * Writes the single `videoCall` chat message that records a 1:1 call. The caller
 * (the Task-10 `useVideoCall` hook, caller role) fires this once when it
 * initiates the call so the audience's `VideoCallBubble` can render and the
 * per-message live subscription has a document to track.
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:782-811
 *   (`addVideoCallMessage`). Returns early `false` when `_peerUser == null`
 *   (`:787`). Otherwise it builds a `ChatMessage` (field set + order from
 *   `:794-807`) and persists it via `sendMessage` -> `ChatRepository
 *   .saveChatMessage(_e2ee, …)`. For a `videoCall`-type message the `sendMessage`
 *   gate (`chat_screen.dart:657`) passes straight through to the same E2EE-aware
 *   save path the text send uses; the optimistic list-insert / scroll / setState
 *   that `sendMessage` also performs are presentation concerns owned by the hook,
 *   not this domain use case.
 *
 * The message has empty `content`, so there is nothing to encrypt — the value
 * handed to `ChatService.saveChatMessage` is the empty string `''`, NOT `null`.
 * Flutter's `chat_repository.dart:20-25` takes the `else` branch for empty
 * content (`messageToSave = chatMessage`, `content` stays `''`), and
 * `chat_data_source.dart:35` writes `'last_content': chatMessage.content`, i.e.
 * `last_content: ''`. A `videoCall` message is the latest message when this
 * runs, so it sets `last_content` on the shared chat node; writing `null` there
 * crashes Flutter's non-nullable `ChatMessageNode.lastContent` parse
 * (`chat_message_node.dart:42`). The service maps `''` to `last_content: ''`
 * and `content: ''` (preserving the empty-string-vs-null distinction).
 *
 * Field-naming parity: `videoCallId` / `callerStreamingId` / `isLive` carry no
 * `@JsonKey` in Flutter (`chat_message.dart:31-35`), so each Firestore field
 * equals its Dart property name; the web DTO / entity / remapper already mirror
 * that 1:1. `type` is `ChatMessageType.videoCall` (ordinal 5).
 *
 * Pure-domain contract: the use case builds + persists the entity and returns
 * the built entity (or `null` when there is no peer), so the caller can seed its
 * own optimistic store — the web analog of Flutter's `_listItems.insert(0,
 * chatMessage)` in `sendMessage` (`chat_screen.dart:665-669`). It does not
 * dispatch to Redux or touch the optimistic UI list (presentation concerns).
 *
 * Returning the entity (rather than only the save boolean) is parity-critical:
 * the three write-back use cases resolve their target message from the entity
 * the caller stored at create time (Flutter resolves it from `_listItems`, fed
 * by both the chat screen's full-fidelity subscription AND `sendMessage`'s
 * optimistic insert). Without the caller-side seed the create-time `added`
 * change is invisible to the `modified`-only lifecycle subscription and the
 * write-backs would no-op (see business-parity finding 1).
 */

import { ChatService } from '@/domain/services/chat/chatService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';

export interface AddVideoCallMessageParams {
  /** The caller's local Agora streaming id (`callerStreamingId`). */
  callerStreamingId: number;
  /** The call session id, stored on the message as `videoCallId`. */
  callMessageId: string;
  /** The current (caller) user — supplies `idFrom`. */
  currentUser: UserEntity;
  /**
   * The conversation peer (audience) — supplies `idTo`. When `null` the use case
   * returns `false` without writing (parity `chat_screen.dart:787`).
   */
  peerUser: UserEntity | null;
  /** Chat-users context forwarded to the save path for the metadata doc. */
  chatUsersInfo: ChatUsersInfoEntity;
}

export class AddVideoCallMessageUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Build and persist the `videoCall` message.
   *
   * @returns `null` when `peerUser == null` (parity `chat_screen.dart:787` —
   *   nothing written), otherwise the built `ChatMessageEntity`. The caller
   *   stores the returned entity as its optimistic resolution source for the
   *   write-back use cases (the web analog of Flutter's `_listItems.insert`).
   *   The fire-and-forget save still runs; its success boolean is not surfaced
   *   because the entity (not the boolean) is what the write-backs need.
   */
  async execute({
    callerStreamingId,
    callMessageId,
    currentUser,
    peerUser,
    chatUsersInfo,
  }: AddVideoCallMessageParams): Promise<ChatMessageEntity | null> {
    // Parity: `chat_screen.dart:787` — abort without writing when there is no peer.
    if (peerUser == null) {
      return null;
    }

    const idFrom = currentUser.firebaseId;
    const idTo = peerUser.firebaseId;
    const timestamp = Date.now();

    // Field set + order mirror `chat_screen.dart:794-807` exactly.
    const chatMessage: ChatMessageEntity = {
      documentId: generateUuidV1(),
      content: '',
      chatId: ChatUtils.getGroupChatId(idFrom, idTo),
      videoCallId: callMessageId,
      callerStreamingId,
      idFrom,
      idTo,
      timestamp,
      type: ChatMessageType.videoCall,
      seenByPeer: false,
      isLive: true,
      users: [idFrom, idTo],
      // `success` / `giftInstances` carry the entity defaults Flutter's
      // `ChatMessage()` constructor seeds (success = true, giftInstances = 1).
      success: true,
      giftInstances: 1,
    };

    // Empty content -> nothing to encrypt -> empty-string `''` (NOT `null`).
    // Flutter writes `content: ''` / `last_content: ''` for this message
    // (chat_repository.dart:20-25 else-branch + chat_data_source.dart:35); a
    // `null` `last_content` crashes Flutter's non-nullable parse
    // (chat_message_node.dart:42). The service maps `''` to `last_content: ''`.
    await this.chatService.saveChatMessage(chatMessage, chatUsersInfo, '');

    // Return the built entity so the caller can seed its optimistic resolution
    // source (Flutter's `_listItems.insert`), making the message available to
    // the write-back use cases immediately — before the Firestore round-trip.
    return chatMessage;
  }
}

// Export singleton instance
export const addVideoCallMessageUseCase = new AddVideoCallMessageUseCase();
